import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, AlertTriangle, PackageCheck, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useCjCart } from "../../context/CjCartContext";
import { api } from "../../lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const MarketCart = () => {
  const { items, remove, setQty, subtotal } = useCjCart();
  const [statusMap, setStatusMap] = useState({});   // handle -> {availability, purchasable}
  const [allPurchasable, setAllPurchasable] = useState(false);
  const [readsDisabled, setReadsDisabled] = useState(false);
  const [budgetExhausted, setBudgetExhausted] = useState(false);
  const [resetAt, setResetAt] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [alternatives, setAlternatives] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Genuine on-demand verification of the ACTUAL cart items (deduped + cached + persisted).
  const verifyCart = useCallback(async () => {
    const handles = items.map((i) => i.handle).filter(Boolean);
    if (!handles.length) { setStatusMap({}); setAllPurchasable(false); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/cj/verify-cart", { handles });
      setStatusMap(data.status || {});
      setAllPurchasable(!!data.all_purchasable);
      setReadsDisabled(!!data.reads_disabled);
      setBudgetExhausted(!!data.budget_exhausted);
      setResetAt(data.reset_at || null);
    } catch { /* keep blocked on failure */ }
    finally { setVerifying(false); }
  }, [items]);

  useEffect(() => { verifyCart(); }, [verifyCart]);

  const statusOf = (i) => (statusMap[i.handle]?.availability || "pending");
  const pendingItems = items.filter((i) => !statusMap[i.handle]?.purchasable);
  // Authoritative client gate — backend re-enforces regardless.
  const blocked = !allPurchasable || pendingItems.length > 0 || items.length === 0;

  const proceed = async () => {
    if (busy || verifying || blocked) return;   // prevents keyboard/programmatic/repeat clicks
    setBusy(true); setAlternatives([]);
    try {
      let varMap = {};
      const needMap = items.filter((i) => !i.variantId).map((i) => i.handle).filter(Boolean);
      if (needMap.length) {
        try {
          const { data } = await api.post("/cj/checkout-map", { handles: needMap });
          varMap = data.map || {};
        } catch (e) {
          if (e?.response?.status === 503) throw e;
          varMap = {};
        }
      }
      const purchasable = items.filter((i) => i.variantId || varMap[i.handle]);
      if (!purchasable.length) {
        toast.info("Your items are still being stock-verified — nothing was removed.");
        setBusy(false); verifyCart(); return;
      }
      const lines = purchasable
        .map((i) => ({ variantId: i.variantId || varMap[i.handle], quantity: i.quantity }))
        .filter((l) => l.variantId);
      const { data } = await api.post("/shop/checkout", { lines });
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
      else setBusy(false);
    } catch (e) {
      if (e?.response?.status === 503) {
        const wait = Number(e?.response?.headers?.["retry-after"]) || 5;
        toast.error(`We're confirming stock — please retry checkout in ~${wait}s.`);
      }
      setBusy(false);
      verifyCart();
    }
  };

  const loadAlternatives = async () => {
    try {
      const handles = pendingItems.map((i) => i.handle).filter(Boolean).join(",");
      if (!handles) return;
      const { data } = await api.get(`/cj/alternatives?handles=${encodeURIComponent(handles)}&limit=3`);
      setAlternatives(data.alternatives || []);
    } catch { /* best-effort */ }
  };
  useEffect(() => { if (pendingItems.length) loadAlternatives(); /* eslint-disable-next-line */ }, [statusMap]);

  const Badge = ({ status }) => {
    if (status === "in_stock") return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600 mt-0.5" data-testid="cart-badge-in-stock"><PackageCheck className="w-3 h-3" /> In stock</span>
    );
    if (status === "sold_out") return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-taven-orange mt-0.5" data-testid="cart-badge-sold-out"><AlertTriangle className="w-3 h-3" /> Sold out</span>
    );
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 mt-0.5" data-testid="cart-badge-verifying"><Clock className="w-3 h-3" /> Stock verification pending</span>
    );
  };

  const checkoutLabel = busy ? "Processing…" : verifying ? "Verifying stock…" : blocked ? "Stock verification required" : "Checkout";

  if (items.length === 0) return (
    <div className="max-w-3xl mx-auto px-4 py-16" data-testid="market-cart-empty">
      <div className="text-center py-8">
        <h1 className="text-2xl font-black font-display">Your cart is empty</h1>
        <button onClick={() => navigate("/shop")} data-testid="cart-browse-btn" className="mt-4 px-6 py-2 rounded-full bg-black text-white text-sm font-bold">Browse Products</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-3 gap-8" data-testid="market-cart">
      <div className="lg:col-span-2">
        <h1 className="text-2xl md:text-3xl font-black font-display tracking-tight mb-6">Your Cart</h1>
        <div className="divide-y divide-black/5">
          {items.map((i) => (
            <div key={i.key} className="flex gap-4 py-4" data-testid={`cart-item-${i.key}`}>
              <img src={i.image || undefined} alt={i.title} className="w-20 h-24 object-cover rounded-lg bg-taven-gray" />
              <div className="flex-1">
                <p className="text-sm font-medium line-clamp-2">{i.title}</p>
                <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{i.source === "shopify" ? "TAVEN Store" : "TAVEN Marketplace"}</span>
                <div><Badge status={statusOf(i)} /></div>
                <p className="text-sm font-black mt-1">{money(i.price)}</p>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center border border-black/10 rounded-full">
                    <button onClick={() => setQty(i.key, i.quantity - 1)} data-testid={`cart-dec-${i.key}`} className="p-1.5"><Minus className="w-3 h-3" /></button>
                    <span className="text-sm w-6 text-center" data-testid={`cart-qty-${i.key}`}>{i.quantity}</span>
                    <button onClick={() => setQty(i.key, i.quantity + 1)} data-testid={`cart-inc-${i.key}`} className="p-1.5"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => remove(i.key)} data-testid={`cart-remove-${i.key}`} className="text-muted-foreground hover:text-taven-orange"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-taven-gray rounded-2xl p-6 h-fit" data-testid="cart-summary">
        <h2 className="text-lg font-black font-display mb-4">Order Summary</h2>
        <div className="flex justify-between text-sm mb-1"><span>Subtotal</span><span className="font-bold" data-testid="cart-subtotal">{money(subtotal)}</span></div>
        <div className="flex justify-between text-xs text-muted-foreground mb-4"><span>Shipping</span><span>Calculated at checkout</span></div>

        <button data-testid="market-checkout-btn" onClick={proceed} disabled={busy || verifying || blocked} aria-disabled={busy || verifying || blocked}
          className={`w-full h-11 rounded-full text-sm font-bold uppercase tracking-wider transition-colors ${(busy || verifying || blocked) ? "bg-black/30 text-white cursor-not-allowed" : "bg-black text-white hover:bg-taven-gold hover:text-black"}`}>
          {checkoutLabel}
        </button>
        <p className="mt-2 text-[11px] text-muted-foreground text-center">Stock is verified before checkout so every order can be fulfilled.</p>

        {(blocked && items.length > 0) && (
          <div className="rounded-xl bg-white border border-amber-500/50 p-4 mt-4" data-testid="checkout-pending-notice">
            <p className="font-bold text-sm mb-1 flex items-center gap-1.5 text-amber-600"><Clock className="w-4 h-4" /> {pendingItems.length} item{pendingItems.length !== 1 ? "s" : ""} awaiting stock verification</p>
            <p className="text-xs text-black/70 mb-3">Checkout unlocks automatically once every item has confirmed positive stock. Nothing is removed from your cart.</p>
            <button onClick={verifyCart} disabled={verifying} data-testid="cart-retry-verify" className="inline-flex items-center gap-1.5 text-xs font-bold underline text-black/70 hover:text-taven-gold disabled:opacity-50">
              <RefreshCw className={`w-3 h-3 ${verifying ? "animate-spin" : ""}`} /> {verifying ? "Verifying…" : "Retry verification"}
            </button>
            {readsDisabled && <p className="mt-2 text-[11px] text-black/50">Live verification is currently off — items stay browsable but can't be purchased yet.</p>}
            {budgetExhausted && (
              <p className="mt-2 text-[11px] text-amber-700 font-semibold" data-testid="cart-budget-reset-note">
                Today's live stock-check capacity is fully used. Verification retries automatically{resetAt ? ` after ${new Date(resetAt).toLocaleString()}` : " shortly"}.
              </p>
            )}
            {alternatives.length > 0 && (
              <div className="mt-3" data-testid="cart-alternatives">
                <p className="text-xs font-bold uppercase tracking-wider text-black/60 mb-2">In-stock alternatives</p>
                <div className="space-y-2">
                  {alternatives.map((a) => (
                    <button key={a.handle} data-testid={`cart-alternative-${a.handle}`} onClick={() => navigate(`/market/product/${a.handle}`)}
                      className="w-full flex items-center gap-3 text-left rounded-lg border border-black/10 p-2 hover:border-taven-gold hover:bg-taven-gray/40 transition-colors">
                      <img src={a.image || undefined} alt={a.title} className="w-12 h-14 object-cover rounded-md bg-taven-gray" />
                      <div className="flex-1 min-w-0"><p className="text-xs font-medium line-clamp-2">{a.title}</p><p className="text-xs font-black mt-0.5">{money(a.price)}</p></div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketCart;
